import { useCallback, useEffect, useRef, useState } from 'react';
import { TimelinesWrapper } from '@/partials/timelines/default/item';
import { renderMultiAvatars } from '@/utils';
import notificationService from '@/services/notification/notificationService';
import { Skeleton } from '@/components/ui/skeleton';
import { ClockFading, Loader2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const DEFAULT_PAGE_SIZE = 10;

const extractItems = (payload) => {
    if (Array.isArray(payload)) return payload;
    if (Array.isArray(payload?.data)) return payload.data;
    return [];
};

const hasNextPage = (payload, items, pageSize) => {
    if (payload?.next_page_url) return true;
    if (payload?.links?.next) return true;
    if (payload?.meta?.current_page && payload?.meta?.last_page) {
        return payload.meta.current_page < payload.meta.last_page;
    }
    return items.length === pageSize;
};

const formatMessage = (item) => {
    const data = item?.data || {};
    const msg = (
        data.message ||
        data.messages ||
        item?.message ||
        (data.contact || data.matter
            ? `Intake Record Received. Contact: '${data.contact || '-'}' - Matter: '${data.matter || '-'}'`
            : '')
    );
    return typeof msg === 'string' ? msg.trim() : msg;
};

const formatTime = (value) => {
    if (!value) return 'No timestamp available';
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return 'No timestamp available';
    return d.toLocaleString('en-US', {
        month: 'short',
        day: '2-digit',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
    });
};

const ActivityNotificaiton = ({ pageSize = DEFAULT_PAGE_SIZE }) => {
    const navigate = useNavigate();
    const [items, setItems] = useState([]);
    const [page, setPage] = useState(1);
    const [loading, setLoading] = useState(false);
    const [initialLoading, setInitialLoading] = useState(true);
    const [hasMore, setHasMore] = useState(true);
    const containerRef = useRef(null);
    const sentinelRef = useRef(null);
    const observerRef = useRef(null);
    const loadingRef = useRef(false);
    const hasMoreRef = useRef(true);
    const pageRef = useRef(1);

    const loadPage = useCallback(async (nextPage) => {
        if (loadingRef.current || !hasMoreRef.current) return;
        loadingRef.current = true;
        setLoading(true);
        try {
            const response = await notificationService.getAllNotifications({
                page: nextPage,
                pageSize,
            });

            if (response?.status) {
                const payload = response?.data ?? response;
                const nextItems = extractItems(payload);

                const nextHasMore = hasNextPage(payload, nextItems, pageSize);
                setItems((prev) => (nextPage === 1 ? nextItems : [...prev, ...nextItems]));
                setHasMore(nextHasMore);
                setPage(nextPage);
                hasMoreRef.current = nextHasMore;
                pageRef.current = nextPage;
            }
        } catch (error) {
            console.error('Error fetching notifications', error);
        } finally {
            loadingRef.current = false;
            setLoading(false);
            setInitialLoading(false);
        }
    }, [pageSize]);

    useEffect(() => {
        loadPage(1);
    }, [loadPage]);

    useEffect(() => {
        if (!hasMore) {
            observerRef.current?.disconnect();
            return;
        }
        if (!sentinelRef.current) return;
        observerRef.current?.disconnect();

        observerRef.current = new IntersectionObserver(
            (entries) => {
                if (entries[0]?.isIntersecting) {
                    loadPage(pageRef.current + 1);
                }
            },
            { root: containerRef.current, rootMargin: '200px' }
        );

        observerRef.current.observe(sentinelRef.current);
        return () => observerRef.current?.disconnect();
    }, [hasMore, items.length, loadPage]);

    const isEmpty = !initialLoading && items.length === 0;

    const getNotificationRedirectPath = (notification) => {
        // Newer notifications carry their own path (demand-letter approvals, OL-119).
        if (notification?.data?.link) return notification.data.link;
        const contactId = notification?.data?.contact_id || notification?.contact_id;

        switch (notification?.type) {
            case 'App\\Notifications\\ImportCompletedNotification':
                return '/data-import/details';
            case 'App\\Notifications\\ReferralRequestNotification':
                return '/account/home/referral-request';
            case 'App\\Notifications\\ReferralContactNotification':
                return '/account/home/referred-contact-list';
            case 'App\\Notifications\\DocumentRecievedNotification':
            case 'App\\Notifications\\SignedDocumentNotification':
                return '/documents/e-signature-documents';
            case 'App\\Notifications\\DocumentNotification': {
                return contactId ? `/contacts/${contactId}?tab=Files` : null;
            }
            case 'App\\Notifications\\IntakeFormFilledNotification': {
                return contactId ? `/contacts` : null;
            }
            case 'App\\Notifications\\ReminderDueNotification': {
                const type = notification?.data?.remindable_type || notification?.remindable_type;
                const parentId = notification?.data?.remindable_id || notification?.remindable_id;
                if (!type || !parentId) return '/';
                return type === 'contact'
                    ? `/contacts/${parentId}?tab=Reminders`
                    : `/matters/detail/${parentId}?tab=Reminders`;
            }
            case 'App\\Notifications\\CommentMentionNotification': {
                const type = notification?.data?.commentable_type || notification?.commentable_type;
                const parentId = notification?.data?.commentable_id || notification?.commentable_id;
                if (!type || !parentId) return '/';
                return type === 'contact'
                    ? `/contacts/${parentId}?tab=Discussion`
                    : `/matters/detail/${parentId}?tab=Discussion`;
            }
            default:
                return null;
        }
    };

    const handleNotificationClick = (notification) => {
        const redirectPath = getNotificationRedirectPath(notification);

        if (redirectPath) {
            navigate(redirectPath);
        }
    };

    return (
        <div className="card w-full">
            <div className="card-header px-3.5">
                <h3 className="card-title">Notifications</h3>
            </div>
            <div ref={containerRef} className="card-body px-3 pt-4 pb-3 overflow-auto h-[30rem] custom-scrollbar">
                {initialLoading ? (
                    <div className="flex flex-col gap-4">
                        {[1, 2, 3, 4, 5].map((row) => (
                            <div key={row} className="flex items-start relative">
                                <div className="w-9 start-0 top-9 absolute bottom-0 rtl:-translate-x-1/2 translate-x-1/2 border-s border-s-gray-300"></div>
                                <div className="flex items-center justify-center shrink-0 rounded-full size-9">
                                    <Skeleton className="size-9 rounded-full" />
                                </div>
                                <div className="ps-2.5 text-md grow">
                                    <div className="flex flex-col gap-2 mb-2">
                                        <Skeleton className="h-3 w-3/4" />
                                        <Skeleton className="h-3 w-1/2" />
                                        <div className="flex gap-2">
                                            <Skeleton className="h-3 w-24" />
                                            <Skeleton className="h-3 w-28" />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                ) : isEmpty ? (
                    <div className="text-sm text-gray-600 italic">No notifications found.</div>
                ) : (
                    <div className="flex flex-col">
                        {items.map((activity, index) => {
                            const userFullName = `${activity?.user?.first_name || ''} ${activity?.user?.last_name || ''}`.trim() || 'Unknown user';
                            const avatarNode = (
                                <div className="">
                                    {renderMultiAvatars(activity?.user)}
                                </div>
                            );
                            const message = formatMessage(activity);
                            return (
                                <TimelinesWrapper icon="share" iconNode={avatarNode} line={true} key={`${activity?.id || index}`}>
                                    <button
                                        type="button"
                                        onClick={() => handleNotificationClick(activity)}
                                        className="group w-full text-left rounded-md transition-colors hover:bg-light p-1"
                                    >
                                        <div className="flex items-center gap-3">
                                            <div className="flex-1 min-w-0">
                                                <div className="text-sm text-gray-900 font-medium truncate">
                                                    {userFullName}
                                                </div>
                                                {/* <div className="text-xs text-gray-500 truncate">
                                                    {activity?.user?.email || 'No email'}
                                                </div> */}
                                            </div>
                                        </div>
                                        <div className="flex flex-col gap-1">
                                            <div className="text-sm text-gray-800 group-hover:text-primary">
                                                {message || 'Notification received.'}
                                            </div>
                                            <span className="flex items-center gap-2 text-xs text-gray-600">
                                                <ClockFading className='size-4' />
                                                {formatTime(activity?.created_at)}
                                            </span>
                                        </div>
                                    </button>
                                </TimelinesWrapper>
                            );
                        })}
                        {hasMore && (
                            <div ref={sentinelRef} className="h-6" />
                        )}
                        {loading && !initialLoading && (
                            <div className="text-xs text-gray-500 flex items-center justify-center gap-1">
                                <Loader2 className="h-3 w-3 animate-spin" />
                                Loading more...
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export { ActivityNotificaiton };
